// @ts-check

/**
 * @file Renders the project detail route.
 */

import _ from "gettext-universal/build/src/translate.js"
import gettextConfig from "gettext-universal/build/src/config.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo, useEffect, useMemo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import useLiveQuery from "velocious/build/src/database/use-live-query.js"
import Link from "../../../src/components/link"
import Button from "../../../src/components/button"
import buildRoutePath from "../../../src/shared/build-route-path"
import Card from "../../../src/components/card"
import DetailValue from "../../../src/components/detail-value"
import errorMessage from "../../../src/shared/error-message"
import Pagination from "../../../src/components/pagination"
import TaskCard from "../../../src/components/task-card"
import TaskFilters from "../../../src/components/tasks/filters"
import {Project as RemoteProject} from "../../../src/frontend-models/project"
import {Organization} from "../../../src/frontend-models/organization"
import {ProjectAutoassignedUser} from "../../../src/frontend-models/project-autoassigned-user"
import {UserProjectLink} from "../../../src/frontend-models/user-project-link"
import {requiredRouteParam} from "../../../src/shared/route-params"
import {DEFAULT_TASK_SORT_KEY} from "../../../src/shared/task-sort"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../../../src/components/screen-title"
import {projectReplicaCountSource, projectReplicaRowsSource} from "../../../src/database/project-replica-reads"
import Board from "../../../src/models/board"
import Comment from "../../../src/models/comment"
import Project from "../../../src/models/project"
import TaskAssignedUser from "../../../src/models/task-assigned-user"
import TaskType from "../../../src/models/task-type"
import Task from "../../../src/models/task"
import Timelog from "../../../src/models/timelog"
import {useProjectSyncOnline} from "../../../src/sync/project-sync-connectivity"
import {useActiveProjectReplica} from "../../../src/sync/project-sync-lifecycle"

/** @import {BoardThemeColors} from "../../../src/shared/theme" */

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import UserModel from "../../../src/frontend-models/user" */
/** @import RecordModel from "velocious/build/src/database/record/index.js" */
/** @import ModelClassQuery from "velocious/build/src/database/query/model-class-query.js" */
/** @type {Record<string, object>} */
const styles = {}
const TASKS_PAGE_SIZE = 30
const VISIBLE_TASK_STATES = ["open", "confirmed", "waiting"]

/**
 * Formats a cents-and-currency hourly price.
 * @param {number | null | undefined} cents Hourly price in cents.
 * @param {string | null | undefined} currency Currency code to append.
 * @returns {string} Formatted hourly price.
 */
function formatPricePerHour(cents, currency) {
  if (cents == null) return "-"
  const amount = new Intl.NumberFormat(gettextConfig.getLocale() || undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  }).format(cents / 100)
  return currency ? `${amount} ${currency}` : amount
}

/** @typedef {{[key: string]: unknown}} ProjectsShowScreenProps */

/**
 * @typedef {object} ProjectsShowScreenState
 * @property {ProjectAutoassignedUser[]} autoassignedUsers Autoassigned project users.
 * @property {RemoteProject | null} financialProject Online-only financial projection when available.
 * @property {Organization | null} organization Project organization when assigned.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 * @property {{name: string, sortKey: import("../../../src/shared/task-sort").TaskSortKey, states: string[]}} taskFilters Active project task filters.
 * @property {number} tasksPage Current 1-based pagination page for the tasks list.
 * @property {UserProjectLink[]} userProjectLinks Project member links.
 */

/**
 * Project show screen.
 * @augments {ShapeComponent<ProjectsShowScreenProps, ProjectsShowScreenState>}
 */
class ProjectsShowScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {string} */
  projectID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {InstanceType<typeof Board> | null} */
  board = null
  /** @type {Record<string, number>} */
  assignedUsersCounts = {}
  /** @type {Record<string, number>} */
  commentsCounts = {}
  loading = true
  online = true
  /** @type {InstanceType<typeof Project> | null} */
  project = null
  readError = /** @type {Error | null} */ (null)
  /** @type {InstanceType<typeof TaskType>[]} */
  taskTypes = []
  /** @type {InstanceType<typeof Task>[]} */
  tasks = []
  tasksTotalCount = 0
  /** @type {Record<string, number>} */
  timelogsCounts = {}
  /** @type {ProjectsShowScreenState} */
  state = {
    autoassignedUsers: /** @type {ProjectAutoassignedUser[]} */ ([]),
    financialProject: /** @type {RemoteProject | null} */ null,
    organization: /** @type {Organization | null} */ null,
    status: /** @type {{kind: "error", text: string} | null} */ null,
    taskFilters: {
      name: "",
      sortKey: DEFAULT_TASK_SORT_KEY,
      states: []
    },
    tasksPage: 1,
    userProjectLinks: /** @type {UserProjectLink[]} */ ([])
  }

  /** Declares catalog, tenant-local core, and explicitly excluded online-only reads. */
  setup() {
    useLocale()
    const params = useLocalSearchParams()
    const projectID = requiredRouteParam(params, "id", "Project ID")
    const replica = useActiveProjectReplica(projectID)
    const online = useProjectSyncOnline()
    const {name, sortKey, states} = this.s.taskFilters
    const tasksPage = this.s.tasksPage
    const statesKey = states.join(",")
    const projectQuery = useMemo(() => Project.where({id: projectID}).limit(1), [projectID])
    const taskQueryParams = useMemo(() => projectTaskQueryParams({
      name,
      projectID,
      states: statesKey ? statesKey.split(",") : []
    }), [name, projectID, statesKey])
    const boardSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: Board,
      projectId: projectID,
      query: (boards) => boards.ransack({projectIdEq: projectID, s: "id asc"}).limit(1),
      replica
    }), [projectID, replica])
    const taskTypesSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: TaskType,
      projectId: projectID,
      query: (taskTypes) => taskTypes.ransack({projectIdEq: projectID, s: "name asc, id asc"}),
      replica
    }), [projectID, replica])
    const tasksSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: Task,
      projectId: projectID,
      query: (tasks) => orderProjectTasks(tasks.ransack(taskQueryParams), sortKey).offset((tasksPage - 1) * TASKS_PAGE_SIZE).limit(TASKS_PAGE_SIZE),
      replica
    }), [projectID, replica, sortKey, taskQueryParams, tasksPage])
    const tasksCountSource = useMemo(() => replica && projectReplicaCountSource({
      modelClass: Task,
      projectId: projectID,
      query: (tasks) => tasks.ransack(taskQueryParams),
      replica
    }), [projectID, replica, taskQueryParams])
    const projectState = useLiveQuery(projectQuery)
    const boardState = useLiveQuery(boardSource)
    const taskTypesState = useLiveQuery(taskTypesSource)
    const tasksState = useLiveQuery(tasksSource)
    const tasksCountState = useLiveQuery(tasksCountSource)
    const taskIdsKey = tasksState.results.map((task) => task.id()).join(",")
    const assignedUsersSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: TaskAssignedUser,
      projectId: projectID,
      query: (assignments) => whereTaskIds(assignments, taskIdsKey, "taskId"),
      replica
    }), [projectID, replica, taskIdsKey])
    const commentsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: Comment,
      projectId: projectID,
      query: (comments) => taskIdsKey
        ? comments.where({resourceId: taskIdsKey.split(","), resourceType: "Task"})
        : comments.where({id: []}),
      replica
    }), [projectID, replica, taskIdsKey])
    const timelogsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: Timelog,
      projectId: projectID,
      query: (timelogs) => whereTaskIds(timelogs, taskIdsKey, "taskId"),
      replica
    }), [projectID, replica, taskIdsKey])
    const assignedUsersState = useLiveQuery(assignedUsersSource)
    const commentsState = useLiveQuery(commentsSource)
    const timelogsState = useLiveQuery(timelogsSource)
    const queryStates = [projectState, boardState, taskTypesState, tasksState, tasksCountState, assignedUsersState, commentsState, timelogsState]
    const project = projectState.results[0] || null
    const organizationId = project?.organizationId() || null
    const projectPresent = Boolean(project)
    const tasksTotalCount = tasksCountState.results[0] ?? 0
    const loading = !replica || (!project && queryStates.some((state) => state.loading))

    this.params = params
    this.projectID = projectID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    this.online = online
    this.project = project
    this.board = boardState.results[0] || null
    this.taskTypes = taskTypesState.results
    this.tasks = tasksState.results
    this.tasksTotalCount = tasksTotalCount
    this.assignedUsersCounts = countsBy(assignedUsersState.results, (assignment) => assignment.taskId())
    this.commentsCounts = countsBy(commentsState.results, (comment) => comment.resourceId())
    this.timelogsCounts = countsBy(timelogsState.results, (timelog) => timelog.taskId())
    this.loading = loading
    this.readError = queryStates.find((state) => state.error)?.error || null
    useScreenLoading(loading)

    useEffect(() => {
      let cancelled = false
      if (!online || !projectPresent) {
        this.s.autoassignedUsers = []
        this.s.financialProject = null
        this.s.organization = null
        this.s.status = null
        this.s.userProjectLinks = []
        return () => { cancelled = true }
      }
      const loadExcludedOnlineDetails = async () => {
        try {
          const [financialProject, organization, autoassignedUsers, userProjectLinks] = await Promise.all([
            RemoteProject.where({id: projectID}).select(["id", "pricePerHourCents", "pricePerHourCurrency", "pricePerHourTransportCents", "pricePerHourTransportCurrency"]).first(),
            organizationId ? Organization.find(organizationId) : null,
            ProjectAutoassignedUser.where({projectId: projectID}).preload({user: true}).toArray(),
            UserProjectLink.where({projectId: projectID}).preload({user: true}).toArray()
          ])
          if (cancelled) return
          const sortedAutoassignedUsers = [...autoassignedUsers].sort((a, b) =>
            displayName(a.user()).localeCompare(displayName(b.user()))
          )
          const sortedUserProjectLinks = [...userProjectLinks].sort((a, b) =>
            displayName(a.user()).localeCompare(displayName(b.user()))
          )
          this.s.autoassignedUsers = sortedAutoassignedUsers
          this.s.financialProject = financialProject
          this.s.organization = organization
          this.s.status = null
          this.s.userProjectLinks = sortedUserProjectLinks
        } catch (error) {
          if (cancelled) return
          this.s.status = {
            kind: "error",
            text: _("Online-only project details could not be loaded: %{message}", {message: errorMessage(error)})
          }
        }
      }

      void loadExcludedOnlineDetails()
      return () => {
        cancelled = true
      }
    }, [online, organizationId, projectID, projectPresent])
    useEffect(() => {
      const totalPages = Math.max(1, Math.ceil(tasksTotalCount / TASKS_PAGE_SIZE))
      if (!loading && tasksPage > totalPages) this.s.tasksPage = totalPages
    }, [loading, tasksPage, tasksTotalCount])
  }

  /**
   * Applies task filters and resets task pagination.
   * @param {{name: string, sortKey: import("../../../src/shared/task-sort").TaskSortKey, states: string[]}} nextFilters Filter values requested by the shared controls.
   * @returns {void} Nothing.
   */
  onTaskFiltersChangeValue = (nextFilters) => {
    if (
      nextFilters.name === this.s.taskFilters.name &&
      nextFilters.sortKey === this.s.taskFilters.sortKey &&
      sameStringArray(nextFilters.states, this.s.taskFilters.states)
    ) {
      return
    }

    this.s.tasksPage = 1
    this.s.taskFilters = nextFilters
  }

  /**
   * Updates the current tasks page.
   * @param {number} nextPage Next 1-based page number.
   * @returns {void} Nothing.
   */
  onTasksPageChange = (nextPage) => {
    if (nextPage === this.s.tasksPage) return
    this.s.tasksPage = nextPage
  }

  /**
   * Renders the project detail screen.
   * @returns {ReactNode} Project detail screen.
   */
  render() {
    const {autoassignedUsers, financialProject, organization, taskFilters, tasksPage, userProjectLinks} = this.s
    const {board, loading, project, taskTypes, tasks, tasksTotalCount} = this
    const {params, projectID} = this.tt
    const status = this.s.status || (this.readError ? {kind: "error", text: this.readError.message} : null)
    const screenTitle = project ? project.name() : _("Project")
    const taskFiltersActive = Boolean(
      taskFilters.name.trim() ||
      taskFilters.states.length > 0 ||
      taskFilters.sortKey !== DEFAULT_TASK_SORT_KEY
    )
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="projectShowScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="projectShowScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <View
          testID="projectShowScreen/content"
        >
          {status &&
            <Text
              style={styles[`statusError-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="projectShowScreen/status"
            >
              {status.text}
            </Text>
          }
          {project &&
            <Card
              style={styles.cardSpacing ||= {padding: 20}}
              testID="projectShowScreen/card"
            >
              <View
                style={styles.headerRow ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 12,
                  justifyContent: "space-between",
                  marginBottom: 20
                }}
                testID="projectShowScreen/headerRow"
              >
                <Text
                  numberOfLines={2}
                  style={styles[`headerTitle-${this.tt.themeName}`] ||= {
                    flexShrink: 1,
                    fontSize: 24,
                    fontWeight: 700,
                    maxWidth: "100%"
                  }}
                  testID="projectShowScreen/title"
                >
                  {screenTitle}
                </Text>
                <Button
                  disabled={!this.online}
                  href={buildRoutePath(params, `/projects/${projectID}/edit`)}
                  iconName="pen-to-square"
                  label={_("Edit")}
                  testID="projectShowScreen/editButton"
                />
              </View>
              {detailRow({
                colors: this.tt.colors,
                label: _("Name"),
                testID: "name",
                themeName: this.tt.themeName,
                value: project.name() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("State"),
                testID: "state",
                themeName: this.tt.themeName,
                value: translatedProjectState(project.state())
              })}
              <View
                style={styles[`detailRow-${this.tt.themeName}`] ||= {
                  borderBottomColor: this.tt.colors.divider,
                  borderBottomWidth: 1,
                  paddingVertical: 10
                }}
                testID="projectShowScreen/organization/row"
              >
                <Text
                  style={styles[`detailLabel-${this.tt.themeName}`] ||= {
                    color: this.tt.colors.textMuted,
                    fontWeight: 600,
                    marginBottom: 4
                  }}
                  testID="projectShowScreen/organization/label"
                >
                  {_("Organization")}
                </Text>
                {organization ? (
                  <Link
                    href={buildRoutePath(
                      params,
                      `/organizations/${organization.id()}`
                    )}
                    style={styles[`detailLink-${this.tt.themeName}`] ||= {
                      color: this.tt.colors.link,
                      fontSize: 16,
                      fontWeight: 600
                    }}
                    testID="projectShowScreen/organization/link"
                  >
                    {organization.name() || "-"}
                  </Link>
                ) : (
                  <Text
                    style={styles[`detailValue-${this.tt.themeName}`] ||= {
                      fontSize: 16
                    }}
                    testID="projectShowScreen/organization/value"
                  >
                    -
                  </Text>
                )}
              </View>
              {detailRow({
                colors: this.tt.colors,
                label: _("Price per hour"),
                testID: "pricePerHour",
                themeName: this.tt.themeName,
                value: formatPricePerHour(
                  financialProject?.pricePerHourCents(),
                  financialProject?.pricePerHourCurrency()
                )
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Price per hour (transport)"),
                testID: "pricePerHourTransport",
                themeName: this.tt.themeName,
                value: formatPricePerHour(
                  financialProject?.pricePerHourTransportCents(),
                  financialProject?.pricePerHourTransportCurrency()
                )
              })}
              <View
                style={styles.subnav ||= {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 20
                }}
                testID="projectShowScreen/subnav"
              >
                {board &&
                  <Button
                    href={buildRoutePath(
                      params,
                      `/projects/${projectID}/boards/${board.id()}`
                    )}
                    iconName="table-columns"
                    label={_("Board")}
                    testID="projectShowScreen/subnav/boardLink"
                    variant="outline"
                  />
                }
                <Button
                  href={buildRoutePath(
                    params,
                    `/projects/${projectID}/task-types`
                  )}
                  iconName="tags"
                  label={_("Task types")}
                  testID="projectShowScreen/subnav/taskTypesLink"
                  variant="outline"
                />
              </View>
              <View
                style={styles.section ||= {
                  marginTop: 24
                }}
                testID="projectShowScreen/tasksSection"
              >
                <View
                  style={styles.tasksSectionHeader ||= {
                    alignItems: "center",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 12,
                    justifyContent: "space-between",
                    marginBottom: 12
                  }}
                  testID="projectShowScreen/tasksSection/header"
                >
                  <Text
                    style={styles[`tasksSectionTitle-${this.tt.themeName}`] ||= {
                      fontSize: 18,
                      fontWeight: 700
                    }}
                    testID="projectShowScreen/tasksSection/title"
                  >
                    {_("Tasks")}
                  </Text>
                  <Button
                    disabled={!this.online}
                    href={buildRoutePath(
                      params,
                      `/tasks/new?projectId=${encodeURIComponent(projectID)}`
                    )}
                    iconName="plus"
                    label={_("Add task")}
                    testID="projectShowScreen/tasksSection/addTaskButton"
                  />
                </View>
                <TaskFilters
                  onChangeValue={this.tt.onTaskFiltersChangeValue}
                  statePlaceholder={_("Open, confirmed, and waiting")}
                  testID="projectShowScreen/tasksSection/filters"
                  value={taskFilters}
                />
                {tasks.length === 0 && tasksTotalCount === 0 &&
                  <Text
                    style={styles[`emptySectionText-${this.tt.themeName}`] ||= {
                      color: this.tt.colors.textMuted
                    }}
                    testID="projectShowScreen/tasksSection/empty"
                  >
                    {taskFiltersActive ? _("No matching tasks found") : _("No tasks found")}
                  </Text>
                }
                {tasks.map((task) =>
                  <TaskCard
                    assignedUsersCount={this.assignedUsersCounts[String(task.id())] || 0}
                    commentsCount={this.commentsCounts[String(task.id())] || 0}
                    commandsDisabled={!this.online}
                    key={task.id()}
                    params={params}
                    project={project}
                    task={task}
                    testIDPrefix="projectShowScreen"
                    timelogsCount={this.timelogsCounts[String(task.id())] || 0}
                  />
                )}
                <Pagination
                  onPageChange={this.tt.onTasksPageChange}
                  page={tasksPage}
                  pageSize={TASKS_PAGE_SIZE}
                  testID="projectShowScreen/tasksSection/pagination"
                  totalCount={tasksTotalCount}
                />
              </View>
              <View
                style={styles.section}
                testID="projectShowScreen/taskTypesSection"
              >
                <View
                  style={styles.taskTypesSectionHeader ||= {
                    alignItems: "center",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 12,
                    justifyContent: "space-between",
                    marginBottom: 12
                  }}
                  testID="projectShowScreen/taskTypesSection/header"
                >
                  <Text
                    style={styles[`taskTypesSectionTitle-${this.tt.themeName}`] ||= {
                      fontSize: 18,
                      fontWeight: 700
                    }}
                    testID="projectShowScreen/taskTypesSection/title"
                  >
                    {_("Task types")}
                  </Text>
                  <Button
                    disabled={!this.online}
                    href={buildRoutePath(
                      params,
                      `/task-types/new?project_id=${encodeURIComponent(projectID)}`
                    )}
                    iconName="plus"
                    label={_("Add task type")}
                    testID="projectShowScreen/taskTypesSection/addTaskTypeButton"
                  />
                </View>
                {taskTypes.length === 0 &&
                  <Text
                    style={styles[`emptySectionText-${this.tt.themeName}`]}
                    testID="projectShowScreen/taskTypesSection/empty"
                  >
                    {_("No task types found")}
                  </Text>
                }
                {taskTypes.map((taskType) => (
                  <View
                    key={taskType.id()}
                    style={styles.sectionCard}
                    testID={`projectShowScreen/taskType-${taskType.id()}`}
                  >
                    <Text
                      style={styles.sectionCardTitle}
                      testID={`projectShowScreen/taskType-${taskType.id()}/title`}
                    >
                      {String(taskType.name() || _("Unnamed task type"))}
                    </Text>
                  </View>
                ))}
              </View>
              <View
                style={styles.section}
                testID="projectShowScreen/membersSection"
              >
                <View
                  style={styles.membersSectionHeader ||= {
                    alignItems: "center",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 12,
                    justifyContent: "space-between",
                    marginBottom: 12
                  }}
                  testID="projectShowScreen/membersSection/header"
                >
                  <Text
                    style={styles[`membersSectionTitle-${this.tt.themeName}`] ||= {
                      fontSize: 18,
                      fontWeight: 700
                    }}
                    testID="projectShowScreen/membersSection/title"
                  >
                    {_("Members")}
                  </Text>
                  <Button
                    disabled={!this.online}
                    href={buildRoutePath(
                      params,
                      `/projects/${projectID}/members`
                    )}
                    iconName="user-plus"
                    label={_("Add user")}
                    testID="projectShowScreen/membersSection/addUserButton"
                  />
                </View>
                {userProjectLinks.length === 0 &&
                  <Text
                    style={styles[`emptySectionText-${this.tt.themeName}`]}
                    testID="projectShowScreen/membersSection/empty"
                  >
                    {_("No members found")}
                  </Text>
                }
                {userProjectLinks.map((userProjectLink) => (
                  <Link
                    href={buildRoutePath(
                      params,
                      `/users/${userProjectLink.userId()}`
                    )}
                    key={userProjectLink.id()}
                    style={styles.sectionCardLink}
                    testID={`projectShowScreen/member-${userProjectLink.id()}`}
                  >
                    <View style={styles.sectionCard}>
                      <Text
                        style={styles.sectionCardTitle}
                        testID={`projectShowScreen/member-${userProjectLink.id()}/title`}
                      >
                        {displayName(userProjectLink.user())}
                      </Text>
                    </View>
                  </Link>
                ))}
              </View>
              <View
                style={styles.section}
                testID="projectShowScreen/autoassignedUsersSection"
              >
                <View
                  style={styles.autoassignedUsersSectionHeader ||= {
                    alignItems: "center",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 12,
                    justifyContent: "space-between",
                    marginBottom: 12
                  }}
                  testID="projectShowScreen/autoassignedUsersSection/header"
                >
                  <Text
                    style={styles[`autoassignedUsersSectionTitle-${this.tt.themeName}`] ||= {
                      fontSize: 18,
                      fontWeight: 700
                    }}
                    testID="projectShowScreen/autoassignedUsersSection/title"
                  >
                    {_("Autoassigned users")}
                  </Text>
                  <Button
                    disabled={!this.online}
                    href={buildRoutePath(
                      params,
                      `/projects/${projectID}/autoassigned-users`
                    )}
                    iconName="user-plus"
                    label={_("Add user")}
                    testID="projectShowScreen/autoassignedUsersSection/addUserButton"
                  />
                </View>
                {autoassignedUsers.length === 0 &&
                  <Text
                    style={styles[`emptySectionText-${this.tt.themeName}`]}
                    testID="projectShowScreen/autoassignedUsersSection/empty"
                  >
                    {_("No autoassigned users found")}
                  </Text>
                }
                {autoassignedUsers.map((projectAutoassignedUser) => (
                  <Link
                    href={buildRoutePath(
                      params,
                      `/users/${projectAutoassignedUser.userId()}`
                    )}
                    key={projectAutoassignedUser.id()}
                    style={styles.sectionCardLink}
                    testID={`projectShowScreen/autoassignedUser-${projectAutoassignedUser.id()}`}
                  >
                    <View style={styles.sectionCard}>
                      <Text
                        style={styles.sectionCardTitle}
                        testID={`projectShowScreen/autoassignedUser-${projectAutoassignedUser.id()}/title`}
                      >
                        {displayName(projectAutoassignedUser.user())}
                      </Text>
                    </View>
                  </Link>
                ))}
              </View>
            </Card>
          }
        </View>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<ProjectsShowScreenProps>} */
const ProjectsShowScreenComponent = memo(shapeComponent(ProjectsShowScreen))

export default ProjectsShowScreenComponent

/**
 * Builds the stable task-list query contract.
 * @param {{name: string, projectID: string, states: string[]}} args Task filter values.
 * @returns {Record<string, unknown>} Local task query parameters.
 */
function projectTaskQueryParams({name, projectID, states}) {
  /** @type {Record<string, unknown>} */
  const params = {
    projectIdEq: projectID,
    stateIn: states.length > 0 ? states : VISIBLE_TASK_STATES
  }
  const normalizedName = name.trim()
  if (normalizedName) params.nameCont = normalizedName

  return params
}

/**
 * Applies deterministic task ordering with physical SQLite column names.
 * @param {ModelClassQuery<typeof Task>} query Tenant-bound task query.
 * @param {import("../../../src/shared/task-sort").TaskSortKey} sortKey Selected sort.
 * @returns {ModelClassQuery<typeof Task>} Ordered tenant-bound query.
 */
function orderProjectTasks(query, sortKey) {
  if (sortKey === "oldest") {
    return query.order({column: "created_at", direction: "ASC"}).order({column: "id", direction: "ASC"})
  }
  if (sortKey === "name_asc") {
    return query.order({column: "name", direction: "ASC"}).order({column: "id", direction: "ASC"})
  }
  if (sortKey === "name_desc") {
    return query.order({column: "name", direction: "DESC"}).order({column: "id", direction: "DESC"})
  }

  return query.order({column: "created_at", direction: "DESC"}).order({column: "id", direction: "DESC"})
}

/**
 * Restricts a relationship query to the displayed task page.
 * @template {typeof RecordModel} MC - Relationship model class.
 * @param {ModelClassQuery<MC>} query Local relationship query.
 * @param {string} taskIdsKey Comma-separated displayed task ids.
 * @param {string} attribute Relationship task-id attribute.
 * @returns {ModelClassQuery<MC>} Restricted query.
 */
function whereTaskIds(query, taskIdsKey, attribute) {
  return taskIdsKey ? query.where({[attribute]: taskIdsKey.split(",")}) : query.where({id: []})
}

/**
 * Counts local relationship rows by task id.
 * @template T - Relationship record type.
 * @param {T[]} records Local relationship rows.
 * @param {(record: T) => string | null | undefined} taskIdForRecord Task-id reader.
 * @returns {Record<string, number>} Counts keyed by task id.
 */
function countsBy(records, taskIdForRecord) {
  /** @type {Record<string, number>} */
  const counts = {}

  for (const record of records) {
    const taskId = taskIdForRecord(record)
    if (taskId) counts[taskId] = (counts[taskId] || 0) + 1
  }

  return counts
}

/**
 * Translates a cached project state without requiring a server virtual attribute.
 * @param {string | null} state Cached project state.
 * @returns {string} Localized project state.
 */
function translatedProjectState(state) {
  if (state === "active") return _("Active")
  if (state === "inactive") return _("Inactive")
  if (state === "finished") return _("Finished")
  return state || "-"
}

/**
 * Compares two ordered string arrays.
 * @param {string[]} first First array to compare.
 * @param {string[]} second Second array to compare.
 * @returns {boolean} True when both arrays contain the same values in order.
 */
function sameStringArray(first, second) {
  if (first.length !== second.length) return false
  for (let index = 0; index < first.length; index++) {
    if (first[index] !== second[index]) return false
  }
  return true
}

/**
 * Renders a static project detail row.
 * @param {{colors: BoardThemeColors, label: string, testID: string, themeName: string, value: string}} args Row rendering options.
 * @returns {ReactNode} Project detail row.
 */
function detailRow({colors, label, testID, themeName, value}) {
  return (
    <View
      collapsable={false}
      style={styles[`detailRow-${themeName}`] ||= {
        borderBottomColor: colors.divider,
        borderBottomWidth: 1,
        paddingVertical: 10
      }}
      testID={`projectShowScreen/${testID}/row`}
    >
      <Text
        style={styles[`detailLabel-${themeName}`] ||= {
          color: colors.textMuted,
          fontWeight: 600,
          marginBottom: 4
        }}
        testID={`projectShowScreen/${testID}/label`}
      >
        {label}
      </Text>
      <DetailValue
        style={styles[`detailValue-${themeName}`] ||= {
          fontSize: 16
        }}
        testID={`projectShowScreen/${testID}/value`}
        value={value}
      />
    </View>
  )
}

/**
 * Formats a user display name.
 * @param {UserModel | null | undefined} user User to display.
 * @returns {string} Best available display name.
 */
function displayName(user) {
  if (!user) return "-"

  const combinedName = [user.firstName(), user.lastName()]
    .filter(Boolean)
    .join(" ")

  return combinedName || user.email() || "-"
}
